<script lang="ts">
	import classNames from 'classnames';
	import {
		filterArtistMerchItems,
		getArtistMerchCategories,
		isShopMerchCategory,
		type ArtistMerchItemWithCategories,
	} from '../../utils/getArtistMerchCategories';
	import { getArtistSectionCount } from '../../utils/getArtistSectionCount';
	import { getSectionId } from '../../utils/getSectionId';
	import { setArtistSectionCount } from '../../utils/setArtistSectionCount';
	import { ProjectSectionTitle } from '../ProjectSectionTitle';
	import { getVariationPrice } from '#lib/features/merch/utils/getVariationPrice/getVariationPrice.js';
	import { ARTIST_PROJECTS_LIMIT } from '#lib/websites/artists/pages/ArtistDetailPage/utils/artistDetailsPageLoad/artistDetailsPageLoad.js';
	import { transformCatalogueMerch } from '#lib/websites/artists/pages/ArtistDetailPage/utils/transformCatalogueMerch/index.js';
	import type { ShopMerchCategory } from '#lib/websites/shop/constants/categories.js';
	import { ShopRoutes } from '#lib/websites/shop/constants/routes.js';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import { ArtworkCard } from '$global/components/ArtworkCard';
	import { Button } from '$global/components/Button';
	import { Txt } from '$global/components/Txt';
	import type { ArtistsPageData } from '$routes/artists/[slug]/types';

	interface Props {
		dataCy: string;
	}

	let { dataCy }: Props = $props();
	let dataCyPrefix = $derived(`${dataCy}-merch`);
	let artist = $derived(page.data.artist as ArtistsPageData['artist']);
	let catalogue = $derived(page.data.catalogue as ArtistsPageData['catalogue']);
	type CatalogueMerchItem = NonNullable<
		ArtistsPageData['catalogue']
	>['merchItems'][number];
	type FilterableMerchItem = CatalogueMerchItem & ArtistMerchItemWithCategories;
	let merchItems = $derived(
		catalogue?.merchItems as unknown as FilterableMerchItem[] | undefined
	);

	let availableCategories = $derived(getArtistMerchCategories(merchItems));
	let requestedCategory = $derived(page.url.searchParams.get('merchCategory'));
	let selectedCategory = $derived(
		isShopMerchCategory(requestedCategory) &&
			availableCategories.includes(requestedCategory)
			? requestedCategory
			: null
	);
	let categoryItems = $derived(
		filterArtistMerchItems(merchItems, selectedCategory)
	);
	let items = $derived(transformCatalogueMerch(categoryItems));
	let visibleCount = $state(
		getArtistSectionCount('merch') ?? ARTIST_PROJECTS_LIMIT
	);
	let visibleItems = $derived(items.slice(0, visibleCount));
	let canViewMore = $derived(visibleCount < items.length);

	$effect(() => {
		// Re-read on category change as well as artist change: an expanded count
		// carried over from a large category would over-reveal a smaller one.
		// (Category changes navigate to a fresh history entry, so the persisted
		// count starts over; back/forward restores each entry's own count.)
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		artist.id;
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		selectedCategory;
		visibleCount = getArtistSectionCount('merch') ?? ARTIST_PROJECTS_LIMIT;
	});

	const showMore = () => {
		visibleCount += ARTIST_PROJECTS_LIMIT;
		setArtistSectionCount('merch', visibleCount);
	};
	// The merch shop's artist facet is keyed by artist name (string-array
	// filter type 3), so the full collection-level set is one click away.
	let viewAllHref = $derived(
		`${ShopRoutes.Merch}?artist=${encodeURIComponent(artist.name || '')}&artist_type=3${
			selectedCategory
				? `&category=${encodeURIComponent(selectedCategory)}&category_type=3`
				: ''
		}`
	);

	const applyCategory = async (category: ShopMerchCategory | null) => {
		const url = new URL(page.url.href);

		if (category) {
			url.searchParams.set('merchCategory', category);
		} else {
			url.searchParams.delete('merchCategory');
		}

		url.hash = getSectionId(dataCy, 'merch');
		await goto(url, { reset: false });
	};
</script>

{#if items.length > 0}
	<div class="border-b border-gray-200"></div>
	<div>
		<ProjectSectionTitle
			title="MERCH"
			href={viewAllHref}
			dataCy={dataCyPrefix}
			label="View all merch"
		/>
		{#if availableCategories.length > 0}
			<div
				class="mb-8 flex flex-wrap gap-2"
				data-cy={`${dataCyPrefix}-categories`}
				role="group"
				aria-label="Filter artist merchandise by category"
			>
				<button
					type="button"
					aria-pressed={selectedCategory === null}
					data-cy={`${dataCyPrefix}-category-all`}
					class={classNames(
						'rounded-full border px-3 py-1',
						selectedCategory === null
							? 'border-gray-900 bg-gray-900'
							: 'border-gray-200 hover:border-gray-900'
					)}
					onclick={() => applyCategory(null)}
				>
					<Txt
						variant="body3"
						class={classNames(
							'tracking-[.08em] uppercase',
							selectedCategory === null ? 'text-gray-0' : ''
						)}
					>
						All
					</Txt>
				</button>
				{#each availableCategories as category}
					{@const isActive = selectedCategory === category}
					<button
						type="button"
						aria-pressed={isActive}
						data-cy={`${dataCyPrefix}-category-${category.toLowerCase()}`}
						class={classNames(
							'rounded-full border px-3 py-1',
							isActive
								? 'border-gray-900 bg-gray-900'
								: 'border-gray-200 hover:border-gray-900'
						)}
						onclick={() => applyCategory(category)}
					>
						<Txt
							variant="body3"
							class={classNames(
								'tracking-[.08em] uppercase',
								isActive ? 'text-gray-0' : ''
							)}
						>
							{category}
						</Txt>
					</button>
				{/each}
			</div>
		{/if}
		<div
			class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
		>
			{#each visibleItems as merchItem}
				<ArtworkCard
					dataCy={dataCyPrefix}
					image={merchItem.image}
					classes={{
						imageContainer: 'bg-[#efefef]',
					}}
					line1={merchItem.title || ''}
					line2={merchItem?.product?.price ||
						getVariationPrice(merchItem?.variations) ||
						''}
					variants={{
						line2: 'body2',
					}}
					href={`${ShopRoutes.MerchProduct}/${merchItem.slug}`}
					alt={merchItem.title || ''}
				/>
			{/each}
		</div>
		{#if canViewMore}
			<Button
				dataCy={`${dataCyPrefix}-view-more`}
				variant="secondary"
				size="md"
				class="mx-auto mb-8 w-full md:w-fit"
				onclick={showMore}>Show more</Button
			>
		{/if}
	</div>
{/if}
